import React, { useState, useEffect } from 'react';
import { api } from '../App';

function History() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/runs').then(r => setRuns(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const getDuration = (s, e) => {
    if (!s || !e) return '-';
    const ms = new Date(e) - new Date(s);
    const sec = Math.floor(ms / 1000);
    return sec >= 60 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${sec}s`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-default-900">Scrape History</h1>
        <p className="text-default-500 mt-1">Past scrape runs and their results</p>
      </div>

      {runs.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card p-12 text-center">
          <div className="w-16 h-16 bg-default-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-default-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-default-900 mb-1">No runs yet</h3>
          <p className="text-default-500">Run a scrape from the dashboard to see history</p>
        </div>
      ) : (
        <>
          {/* Runs List */}
          <div className="space-y-3">
            {runs.map((run) => (
              <div key={run.id} className="bg-white rounded-2xl shadow-card p-5 card">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      run.status === 'completed' ? 'bg-success-500/10' : 'bg-danger-500/10'
                    }`}>
                      {run.status === 'completed' ? (
                        <svg className="w-6 h-6 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-default-900">{run.date}</span>
                        {run.scheduled && (
                          <span className="px-2 py-0.5 bg-primary-500/10 text-primary-600 text-xs font-semibold rounded-lg">
                            Scheduled
                          </span>
                        )}
                        {run.sheetsPushed && (
                          <span className="px-2 py-0.5 bg-success-500/10 text-success-600 text-xs font-semibold rounded-lg">
                            Synced
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-default-500">
                        <span>{formatDate(run.startedAt)}</span>
                        <span className="w-1 h-1 bg-default-300 rounded-full" />
                        <span>{getDuration(run.startedAt, run.completedAt)}</span>
                        {run.apiCost > 0 && (
                          <>
                            <span className="w-1 h-1 bg-default-300 rounded-full" />
                            <span>€{run.apiCost.toFixed(4)}</span>
                          </>
                        )}
                      </div>
                      {run.error && (
                        <p className="text-sm text-danger-600 mt-2 p-2 bg-danger-500/5 rounded-lg">
                          {run.error}
                        </p>
                      )}
                    </div>
                  </div>

                  {run.status === 'completed' && (
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-xl font-bold text-default-900">{run.scraped || 0}</p>
                        <p className="text-xs text-default-500">Scraped</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-success-600">{run.parsed || 0}</p>
                        <p className="text-xs text-default-500">Parsed</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-xl font-bold ${run.parseErrors > 0 ? 'text-danger-600' : 'text-default-300'}`}>
                          {run.parseErrors || 0}
                        </p>
                        <p className="text-xs text-default-500">Errors</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-2xl shadow-card p-6">
            <h3 className="font-semibold text-default-900 mb-4">Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-default-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-default-900">{runs.length}</p>
                <p className="text-sm text-default-500">Total Runs</p>
              </div>
              <div className="bg-success-500/5 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-success-600">
                  {runs.filter(r => r.status === 'completed').length}
                </p>
                <p className="text-sm text-default-500">Successful</p>
              </div>
              <div className="bg-danger-500/5 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-danger-600">
                  {runs.filter(r => r.status === 'error').length}
                </p>
                <p className="text-sm text-default-500">Failed</p>
              </div>
              <div className="bg-warning-500/5 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-warning-600">
                  €{runs.reduce((s, r) => s + (r.apiCost || 0), 0).toFixed(4)}
                </p>
                <p className="text-sm text-default-500">Total Cost</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default History;
